<template>
  <v-content>
    <v-container fluid>
      <v-row>
        <v-col cols="12" lg="3" md="4" sm="5" class="pb-0 pr-sm-0">
          <v-row no-gutters justify="space-between" align="start" class="mb-3">
            <v-card outlined class="mx-auto" width="100%">
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="overline mb-4">{{ $t("data.data.device") }}</div>
                  <v-list-item-title class="headline mb-1">
                    <device-selection></device-selection>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-row>
          <v-row no-gutters v-if="sensorList.length !== 0">
            <v-card class="mx-auto" width="100%" outlined>
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="overline mb-4">{{ $t("data.data.sensors") }}</div>
                  <v-list-item-title class="headline mb-1">
                    <sensor-picker
                      ref="sensorPicker"
                      :sensor-list="sensorList"
                      :location="location"
                      :selected-sensors="selectedSensors"
                      @toggle-sensor="toggleSensor"
                      :key="forceUpdateSensorPicker"
                    ></sensor-picker>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-row>
        </v-col>

        <v-col cols="12" lg="6" md="8" sm="7">
          <v-row no-gutters justify="center" align="start" v-if="!deviceSerial">
            <v-card
              class="mx-auto"
              width="100%"
              outlined
              tile
              style="min-height: 450px"
            >
              <data-access-empty-state
                :value="true"
                :title="$t('data.data.selectASensorAndTimeRangeToGetStarted')"
                :message="
                  $t(
                    'data.data.chooseOneOrMoreSensorsAndADateRangeFromThePanelToViewYourData',
                  )
                "
                icon="mdi-chart-line-variant"
              />
            </v-card>
          </v-row>
          <v-row no-gutters justify="center" align="start" v-if="deviceSerial">
            <v-card
              class="mx-auto chart-card"
              width="100%"
              outlined
              tile
              :loading="loading || loadingCharts"
            >
              <data-access-empty-state
                v-if="!loading && !loadingCharts && chartOverlayConfig"
                :value="true"
                v-bind="chartOverlayConfig"
              />
              <chart
                :selected-sensors="selectedSensors"
                :location="location"
                :selected-alerts="selectedAlerts"
                :key="forceRefreshCharts"
                :date-interval="dateRange"
                @set-hovered-data="hoveredData = $event"
              ></chart>
            </v-card>
          </v-row>
          <v-row
            no-gutters
            justify="space-between"
            align="start"
            v-if="deviceSerial && showChart"
          >
            <v-card
              class="mx-auto map-card"
              width="100%"
              outlined
              tile
              :loading="loading"
              style="margin-top: -1px"
            >
              <data-access-empty-state
                v-if="!loading && mapOverlayConfig"
                :value="true"
                v-bind="mapOverlayConfig"
              />
              <travel-map
                :set-hovered-data="hoveredData"
                :location="location"
                :key="forceRefresh"
              ></travel-map>
            </v-card>
          </v-row>
          <v-row no-gutters class="hidden-lg-and-up mt-3">
            <v-card class="mx-auto" width="100%" outlined>
              <data-alerts
                :selectedAlerts="alerts"
                :sensor-list="sensorList.sensors"
                :cols="$vuetify.breakpoint.md ? 6 : 12"
                @toggle-sensor="toggleSensor"
              ></data-alerts>
            </v-card>
          </v-row>
        </v-col>
        <v-col cols="3" lg="3" md="3" sm="5" class="hidden-md-and-down pl-0">
          <v-card class="mx-auto" width="100%" outlined>
            <data-alerts
              :selectedAlerts="alerts"
              :sensor-list="sensorList.sensors"
              @toggle-sensor="toggleSensor"
            ></data-alerts>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import DeviceSelection from "@/components/selectors/DeviceSelection";
import SensorPicker from "@/components/pickers/SensorPicker";
import api from "@/utils/api";
import Chart from "@/components/miscellaneous/Chart";
import travelMap from "@/components/maps/TravelMap";
import DataAlerts from "@/components/data/DataAlerts";
import DataAccessEmptyState from "@/components/data/DataAccessEmptyState";
import unitConverter from "@/utils/unitConverter";
import colors from "@/utils/colorGenerator.js";
import { mapGetters } from "vuex";

export default {
  components: {
    DeviceSelection,
    SensorPicker,
    Chart,
    travelMap,
    DataAlerts,
    DataAccessEmptyState,
  },
  data: () => ({
    dateRange: [],
    deviceSerial: null,
    alerts: [],
    selectedAlerts: [],
    sensorList: [],
    location: [],
    loadingCharts: false,
    loading: true,
    showChart: true,
    hoveredData: null,
    selectedSensors: { selected: [], data: [] },
    forceRefresh: 0,
    forceRefreshCharts: 0,
    forceUpdateSensorPicker: 0,
    showChartAccessOverlay: false,
    hasLocationPermission: true,
    hasSensorPermission: true,
  }),
  created() {
    this.handleRoute(this.$route);
  },

  watch: {
    $route(newValue, oldValue) {
      if (oldValue.query.id !== newValue.query.id) {
        this.loadData(false);
      } else if (
        oldValue.query.datefrom !== newValue.query.datefrom ||
        oldValue.query.dateto !== newValue.query.dateto
      ) {
        this.loadData(true);
      }
    },
  },

  computed: {
    ...mapGetters(["getMe"]),

    allSelectedSensorsHaveNoData() {
      return (
        this.selectedSensors.selected.length > 0 &&
        this.selectedSensors.data.every(
          (d) => !d.records || d.records.length === 0,
        )
      );
    },

    chartOverlayConfig() {
      if (!this.hasSensorPermission) {
        return {
          title: this.$t("data.data.sensorDataAccessRequired"),
          message: this.$t(
            "data.data.sensorDataAccessConsentHasNotYetBeenGrantedForThisDevice",
          ),
          icon: "mdi-lock",
        };
      }

      if (this.showChartAccessOverlay) {
        return {
          title: this.$t("data.data.noDataAvailableForTheSelectedTimeRange"),
          message: this.$t(
            "data.data.tryAdjustingTheDateRangeOrSelectingDifferentSensors",
          ),
          icon: "mdi-chart-line-variant",
        };
      }

      if (this.allSelectedSensorsHaveNoData) {
        return {
          title: this.$t("data.data.noDataForSelectedSensors"),
          message: this.$t(
            "data.data.tryAdjustingTheDateRangeOrSelectingDifferentSensors",
          ),
          icon: "mdi-chart-line-variant",
        };
      }

      if (this.selectedSensors.selected.length === 0) {
        return {
          title: this.$t("data.data.selectASensorAndTimeRangeToGetStarted"),
          message: this.$t(
            "data.data.chooseOneOrMoreSensorsAndADateRangeFromThePanelToViewYourData",
          ),
          icon: "mdi-chart-line-variant",
        };
      }

      return null;
    },

    mapOverlayConfig() {
      if (!this.hasLocationPermission) {
        return {
          title: this.$t("data.data.locationDataAccessRequired"),
          message: this.$t(
            "data.data.locationDataAccessConsentHasNotYetBeenGrantedForThisDevice",
          ),
          icon: "mdi-lock",
        };
      }

      if (this.location.length === 0) {
        return {
          title: this.$t("data.data.noDataFoundForThisDateRange"),
          message: this.$t("data.data.noLocationDataAvailable"),
          icon: "mdi-map-marker-off",
        };
      }

      return null;
    },
  },
  methods: {
    setIntialData() {
      this.dateRange = [
        this.$route.query.datefrom,
        this.$moment(this.$route.query.dateto).add(1, "days"),
      ].map((x) => this.$moment(x).format());

      this.deviceSerial = this.$route.query.id;
    },

    loadData(status) {
      if (!this.$route.query.id) {
        this.alerts = [];
        this.selectedAlerts = [];
        this.sensorList = [];
        this.location = [];
        this.deviceSerial = null;
        return;
      }

      this.setIntialData();

      if (!status) {
        this.selectedSensors = { selected: [], data: [] };
      }

      this.showChartAccessOverlay = false;
      this.hasLocationPermission = true;
      this.hasSensorPermission = true;
      this.loading = true;

      const sensorList = this.loadSensorList();
      const location = this.loadLocation();
      const alerts = this.loadAlerts();

      let handleAlerts = (value) => {
        if (value.status) {
          value.data.forEach((element) => {
            element.created = this.$moment(element.created).unix();
          });
          value.data.sort((a, b) => b.created - a.created);
        }
        this.alerts = value.data;
        this.selectedAlerts = value.data;
      };

      let handleSensorList = (value) => {
        if (value.data && value.data.sensors) {
          const unitPreference = this.getMe.user.profile.unit_preference;
          value.data.sensors.forEach((sensor, index) => {
            this.$set(sensor, "color", colors.colorGenerator(index));
            this.$set(
              sensor,
              "last_record_value",
              unitConverter.format(
                sensor.last_record_value,
                sensor.sensor.measure_type,
                unitPreference,
                null,
                null,
                false,
              ),
            );
          });
        }

        this.sensorList = value.data;

        let sensorsToLoad = this.getSelectedSensorsFromLink().filter(
          (sensor) => !this.selectedSensors.selected.includes(sensor),
        );

        sensorsToLoad.forEach((uuid) => this.toggleSensor(uuid));

        if (status && this.selectedSensors.selected.length > 0) {
          const currentlySelected = [...this.selectedSensors.selected];
          this.selectedSensors = { selected: [], data: [] };
          currentlySelected.forEach((uuid) => this.toggleSensor(uuid));
        }
      };

      let handleLocation = (value) => {
        if (value.data.length !== 0) {
          value.data.forEach((element) => {
            element.created = this.$moment(element.created).unix();
            element.position = {
              lat: element.position.latitude,
              lng: element.position.longitude,
            };
          });
          this.location = value.data.filter(
            (item) =>
              item.position &&
              item.position.lat !== 0 &&
              item.position.lng !== 0,
          );
        } else {
          this.location = [];
        }
      };

      Promise.allSettled([sensorList, location, alerts]).then((values) => {
        if (values[0].status !== "rejected") {
          handleSensorList(values[0].value);
          this.hasSensorPermission = true;
          if (
            values[0].value.data &&
            (!values[0].value.data.sensors ||
              values[0].value.data.sensors.length === 0)
          ) {
            this.showChartAccessOverlay = true;
          }
        } else {
          this.sensorList = [];
          this.hasSensorPermission = false;
          this.showChartAccessOverlay = true;
        }

        if (values[1].status !== "rejected") {
          handleLocation(values[1].value);
          this.hasLocationPermission = true;
        } else {
          this.location = [];
          this.hasLocationPermission = false;
        }

        if (values[2].status !== "rejected") {
          handleAlerts(values[2].value);
        } else {
          this.alerts = [];
          this.selectedAlerts = [];
        }

        this.loading = false;
        this.forceRefresh++;
      });
    },

    getSelectedSensorsFromLink() {
      const sensors = this.$route.query && this.$route.query.sensors;
      return sensors && sensors.length > 0 ? sensors.split("%2C") : [];
    },

    handleRoute() {
      if (
        this.$route.query.datefrom &&
        this.$route.query.dateto &&
        this.$route.query.id
      ) {
        this.loadData(false);
      } else {
        this.$router.replace({ name: "data" });
      }
    },

    toggleSensor(sensorUuid) {
      const sensor = this.sensorList.sensors.find((s) => s.uuid === sensorUuid);
      if (!sensor) return;

      if (this.selectedSensors.selected.includes(sensorUuid)) {
        const index = this.selectedSensors.selected.indexOf(sensorUuid);
        this.selectedSensors.selected.splice(index, 1);
        this.selectedSensors.data.splice(index, 1);
        this.$set(sensor, "selected", false);
        this.$set(sensor, "noData", false);
        this.getSelectedSensors(this.selectedSensors);
      } else {
        this.$set(sensor, "loading", true);
        this.loadSensorData(sensorUuid, (resp) => {
          if (resp.data.records.length !== 0) {
            this.$set(sensor, "loading", false);
            this.$set(sensor, "selected", true);
            this.$set(sensor, "noData", false);
            resp.data.records.forEach((item) => {
              item.created = this.$moment(item.created).unix();
            });
            resp.data.color = sensor.color;
            resp.data.name = sensor.name;
            this.selectedSensors.selected.push(sensorUuid);
            this.selectedSensors.data.push(resp.data);
            this.getSelectedSensors(this.selectedSensors);
          } else {
            this.$set(sensor, "noData", true);
            this.$set(sensor, "loading", false);
            this.$set(sensor, "selected", true);
            resp.data.color = sensor.color;
            resp.data.name = sensor.name;
            this.selectedSensors.selected.push(sensorUuid);
            this.selectedSensors.data.push(resp.data);
            this.getSelectedSensors(this.selectedSensors);
          }
        });
      }
    },

    getSelectedSensors(event) {
      this.$set(this.selectedSensors, "selected", event.selected);
      this.$set(this.selectedSensors, "data", event.data);

      if (event.data && event.data.length > 0) {
        const hasData = event.data.some(
          (sensor) =>
            sensor.data &&
            sensor.data.records &&
            sensor.data.records.length > 0,
        );
        if (hasData && this.hasSensorPermission) {
          this.showChartAccessOverlay = false;
        }
      }

      if (event.selected.length !== 0) {
        this.$nextTick(() => {
          this.selectedAlerts = this.alerts.filter((element) =>
            event.selected.find((el) => el === element.device_sensor.uuid),
          );
        });
      } else {
        this.$nextTick(() => {
          this.selectedAlerts = this.alerts;
        });
      }

      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            sensors:
              event.selected.length > 0
                ? encodeURIComponent(event.selected)
                : "",
          }),
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });

      this.forceRefreshCharts++;
    },

    loadAlerts(selectedSensor) {
      return api.getDeviceAlarms(
        this.deviceSerial,
        this.dateRange[0],
        this.dateRange[1],
        false,
        true,
      );
    },

    loadSensorList() {
      return api.getDeviceSensorsList(this.deviceSerial, false);
    },

    loadLocation() {
      return api.getDevicePosition(
        this.deviceSerial,
        this.dateRange[0],
        this.dateRange[1],
        false,
        true,
      );
    },

    loadSensorData(sensorId, callback) {
      this.loadingCharts = true;
      api
        .fetchSensorData(
          this.deviceSerial,
          sensorId,
          this.dateRange[0],
          this.dateRange[1],
        )
        .then((resp) => {
          const unitPreference = this.getMe.user.profile.unit_preference;
          const measureType = resp.data.sensor.measure_type;

          resp.data.last_record_value = unitConverter.format(
            resp.data.last_record_value,
            measureType,
            unitPreference,
            null,
            null,
            false,
          );

          resp.data.records = resp.data.records.map((record) => ({
            ...record,
            value: unitConverter.getConvertedValue(
              record.value,
              measureType,
              unitPreference,
              null,
              resp.data.sensor,
            ),
          }));

          this.loadingCharts = false;
          callback(resp);
          this.forceRefreshCharts++;
        })
        .catch(() => {
          this.loadingCharts = false;
          callback({ data: { records: [], sensor: { uuid: sensorId } } });
          this.forceRefreshCharts++;
        });
    },
  },
};
</script>

<style scoped>
.chart-card {
  border-radius: 4px 4px 0 0 !important;
  overflow: hidden;
}

.map-card {
  border-radius: 0 0 4px 4px !important;
  overflow: hidden;
}
</style>
